import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell as PhBell } from '@phosphor-icons/react';
import { useLanguage } from '../../contexts/LanguageContext';

/*
 * NotificationBell — top-right bell in the desktop header. Polls
 * /api/notifications every 2 min for two lightweight buckets:
 *   - Pending org invitations addressed to the user
 *   - Tasks due within the next 60 minutes
 *
 * "Read" state is tracked client-side in localStorage (`notif:read`)
 * keyed by stable notification IDs so the unread badge clears the
 * moment the user opens the dropdown without needing a server write.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 120_000; // 2 minutes
const READ_KEY = 'edotask:notif:read';

const loadReadSet = () => {
  try {
    const raw = localStorage.getItem(READ_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const saveReadSet = (set) => {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify([...set]));
  } catch {
    /* localStorage may be unavailable (private mode) — ignore */
  }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [data, setData] = useState({ pending_invites: [], tasks_due_soon: [], total_count: 0 });
  const [open, setOpen] = useState(false);
  const [readSet, setReadSet] = useState(loadReadSet);
  const containerRef = useRef(null);

  const ttx = (key, fallback) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/notifications`);
      setData(res.data || { pending_invites: [], tasks_due_soon: [], total_count: 0 });
    } catch (err) {
      // Silent fail — bell stays at its last known state.
      // (Network blips shouldn't surface user-facing errors here.)
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_MS);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Build a stable list of all notif IDs so we can compute the unread count.
  const allIds = [
    ...data.pending_invites.map((i) => `invite:${i.id}`),
    ...data.tasks_due_soon.map((tk) => `task:${tk.id}`),
  ];
  const unreadCount = allIds.filter((id) => !readSet.has(id)).length;

  const markAllRead = () => {
    const next = new Set(readSet);
    allIds.forEach((id) => next.add(id));
    setReadSet(next);
    saveReadSet(next);
  };

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) markAllRead();
      return next;
    });
  };

  const handleInviteClick = (invite) => {
    setOpen(false);
    navigate(`/invitations/${invite.token}`);
  };

  const handleTaskClick = (task) => {
    setOpen(false);
    navigate(`/task/${task.id}`);
  };

  const empty = data.total_count === 0;

  return (
    <div ref={containerRef} className="relative" data-testid="notification-bell-root">
      <button
        type="button"
        onClick={handleToggle}
        data-testid="notification-bell-button"
        aria-label={ttx('notifications', 'Notifications')}
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300 transition-colors"
      >
        <PhBell size={20} weight={unreadCount > 0 ? 'fill' : 'regular'} />
        {unreadCount > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-bell-dropdown"
          className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              {ttx('notifications', 'Notifications')}
            </h3>
            {data.total_count > 0 && (
              <span className="text-xs text-gray-500">
                {data.total_count} {ttx('newLabel', 'new')}
              </span>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {empty && (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                <div className="text-3xl mb-2">🔕</div>
                <p>{ttx('noNotifications', "You're all caught up.")}</p>
              </div>
            )}

            {data.pending_invites.length > 0 && (
              <div>
                <div className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {ttx('teamInvitations', 'Team invitations')}
                </div>
                {data.pending_invites.map((invite) => (
                  <button
                    key={invite.id}
                    onClick={() => handleInviteClick(invite)}
                    data-testid={`notification-invite-${invite.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3"
                  >
                    <span className="text-xl flex-shrink-0">📨</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-900 truncate">
                        {invite.org_name}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {ttx('invitedAsRole', 'Invited as')} <span className="capitalize">{invite.role}</span>
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {data.tasks_due_soon.length > 0 && (
              <div>
                <div className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {ttx('dueSoon', 'Due soon')}
                </div>
                {data.tasks_due_soon.map((task) => (
                  <button
                    key={task.id}
                    onClick={() => handleTaskClick(task)}
                    data-testid={`notification-task-${task.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3"
                  >
                    <span className="text-xl flex-shrink-0">⏰</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-900 truncate">
                        {task.title}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {task.minutes_until <= 0
                          ? ttx('dueNow', 'Due now')
                          : `${ttx('inLabel', 'in')} ${task.minutes_until} ${ttx('minutesShort', 'min')}`}
                        {task.time ? ` · ${task.time}` : ''}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
